import { defineStore } from "pinia";
import { computed, ref, watchEffect } from "vue";
import { isSimulfestSession } from "@/utils/simulfest";
import { useDumbStorage } from "@/composables/use-dumb-storage";
import {
  cookieConsentSerializer,
  createAcceptAllConsent,
} from "@/utils/cookies";
import { detectAdBlock } from "@/utils/misc";
import { getSharedHeaders, setAdBlockDetectedHeader } from "@/utils/headers";
import { APP_CONNECTION_ID_HEADER } from "@f3/shared/identity";

export const useAppStore = defineStore("app", () => {
  const isSimulfest = ref(isSimulfestSession());
  const searchParams = new URLSearchParams(window.location.search);
  const isEmbed = searchParams.get("embed") === "true";

  const hasInteractedWithPage = ref(
    window.hasInteractedWithPage === true ||
      // Assume the user has interacted with the page if it's a Simulfest session
      isSimulfest.value,
  );

  const unableToStartAudioContext = computed(
    () => !hasInteractedWithPage.value,
  );

  // Reactive so consumers that build channel names from it (e.g. the
  // per-connection OTP-status channel) re-subscribe once it is populated.
  // Set synchronously from headers at boot; "" only if the header is missing.
  const connectionId = ref<string>(
    getSharedHeaders()[APP_CONNECTION_ID_HEADER] ?? "",
  );

  const cookieConsent = useDumbStorage("cookie-consent", null, {
    serializer: cookieConsentSerializer,
  });
  const showCookieSettings = ref(false);

  // Embed context: the host site handles consent on their end, so we
  // auto-opt-in here to keep tracking / personalization / analytics
  // functional. Temporary — revisit once embed consent UX is designed.
  if (isEmbed && cookieConsent.value === null) {
    cookieConsent.value = createAcceptAllConsent();
  }

  const timer = ref(0);
  watchEffect((onCleanup) => {
    const interval = setInterval(() => {
      timer.value = (timer.value + 1) % 1000;
    }, 2500);

    onCleanup(() => clearInterval(interval));
  });

  watchEffect((onCleanup) => {
    const element = document;
    if (hasInteractedWithPage.value === true) return;

    const listener = async () => {
      window.hasInteractedWithPage = hasInteractedWithPage.value = true;
    };

    // Use capture phase: https://stackoverflow.com/a/65332405
    element.addEventListener("click", listener, true);
    onCleanup(() => {
      element.removeEventListener("click", listener, true);
    });
  });

  const appHasFocus = ref(
    document.hasFocus() && document.visibilityState === "visible",
  );
  watchEffect((onCleanup) => {
    function handleVisibilityChange() {
      appHasFocus.value = document.visibilityState === "visible";
    }

    function handleFocus() {
      appHasFocus.value = true;
    }

    function handleBlur() {
      appHasFocus.value = false;
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    onCleanup(() => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    });
  });

  // Seed from the `integration_mode` query param (set by the SDK on the embed
  // iframe URL) so it's available synchronously on first render — the embed
  // broker's handshake can still update it later via setIntegrationMode.
  const integrationMode = ref<string | null>(
    searchParams.get("integration_mode"),
  );

  function setIntegrationMode(mode: string | null) {
    integrationMode.value = mode;
  }

  const isAdBlockingDetected = ref(false);
  watchEffect(async () => {
    console.log("[AppStore] Detecting ad-blocking...");
    const detected = await detectAdBlock();

    isAdBlockingDetected.value = detected;

    if (detected) {
      console.log("[AppStore] Ad-blocking detected");
      setAdBlockDetectedHeader(true);
    } else {
      console.log("[AppStore] Ad-blocking not detected");
    }
  });

  return {
    timer,
    cookieConsent,
    showCookieSettings,
    isSimulfest,
    isEmbed,
    integrationMode,
    setIntegrationMode,
    unableToStartAudioContext,
    hasInteractedWithPage,
    isAdBlockingDetected,
    connectionId,
    appHasFocus,
  };
});
